import { CompositeSourceCopy } from '../config/compositeSourceEnums';
import {
  CompositeNodeKind,
  TagRootDiscriminatorOptions,
  humanizeNodeKind,
} from './definitionVocabulary';
import { describeRoot, rootKeyOf } from './definitionModelHelpers';

/**
 * The source step's candidate roots that are not picked from a list the server returns
 * (AVA-560): one row per tag kind a `tag` root can read, and one row for the facility's AI
 * findings. Registers and task boards are still fetched by the wizard; every option, whatever its
 * kind, is keyed by `rootKeyOf` so the step selects by the root's whole identity.
 *
 * These rows need no fetch: the facility is the scope and the tag kinds are the server's closed
 * discriminator list. Whether the facility actually holds any rows of the kind is the preview's
 * answer, exactly as it is for an empty register.
 */

/** The key the source step selects an option by: the root identity the option would set. */
export function rootOptionKey(option) {
  return rootKeyOf({ kind: option.kind, rootRegisterId: option.id });
}

/** Stamps the selection key onto an option built elsewhere (registers, task boards). */
export function withRootOptionKey(option) {
  return { ...option, key: rootOptionKey(option) };
}

/** Tag-kind rows and the AI findings row, ready for the source step. */
export function facilityWideRootOptions() {
  const tagKind = CompositeNodeKind.Tag;
  const findingKind = CompositeNodeKind.Finding;

  // A tag root's id IS its tag kind, so the option id is the discriminator.
  const tagOptions = TagRootDiscriminatorOptions.map((tagType) => withRootOptionKey({
    id: tagType.value,
    kind: tagKind,
    kindLabel: humanizeNodeKind(tagKind),
    family: CompositeSourceCopy.TagsFamilyHeader,
    label: describeRoot({ kind: tagKind, rootRegisterId: tagType.value }),
    selectable: true,
    unavailableReason: null,
  }));

  const findingOption = withRootOptionKey({
    id: null,
    kind: findingKind,
    kindLabel: humanizeNodeKind(findingKind),
    family: CompositeSourceCopy.FindingsFamilyHeader,
    label: CompositeSourceCopy.FindingsRootLabel,
    selectable: true,
    unavailableReason: null,
  });

  return tagOptions.concat(findingOption);
}
